import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, LOCALE_ID } from '@angular/core';
import { MAT_RIPPLE_GLOBAL_OPTIONS, MatNativeDateModule } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, RouteReuseStrategy, withRouterConfig } from '@angular/router';

import {
  CustomRouteReuseStrategy,
  FU_CONFIG,
  FU_DIALOG_OK_CONFIG,
  FU_TOAST_CONFIG,
  FU_TOAST_DEFAULT_CONFIG,
  FU_UPLOADER_CONFIG,
  matGlobalRippleConfig,
  TranslateLoaderService,
  UploaderConfig,
  WINDOW,
} from '@hglobal/fin-ui';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { provideNgxMask } from 'ngx-mask';
import { NgxPermissionsModule } from 'ngx-permissions';

import { environment } from '@kr/environment';
import { AUTH_TOKEN_KEY, AuthApiMockService, DictionariesApiService, DictionariesMockApi, DictionariesStore } from '@kr/store';

import { appRoutes } from './app.routes';
import { APP_HTTP_INTERCEPTORS, APP_INITIALIZERS } from './init';
import { AUTH_API, AuthApiService } from './store/auth/api';
import { DICTIONARIES_API } from './store/dictionaries/api';

export const appConfig: ApplicationConfig = {
  providers: [
    APP_INITIALIZERS,
    APP_HTTP_INTERCEPTORS,
    provideRouter(
      appRoutes,
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
    ),
    { provide: LOCALE_ID, useValue: 'en' },
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy,
    },
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: matGlobalRippleConfig,
    },
    {
      provide: FU_CONFIG,
      useValue: {
        apiHost: environment.apiHost,
        useMocks: environment.useMocks,
        production: environment.production,
      },
    },
    {
      provide: FU_TOAST_CONFIG,
      useValue: { ...FU_TOAST_DEFAULT_CONFIG, closeIconName: 'close-square' },
    },
    {
      provide: FU_UPLOADER_CONFIG,
      useFactory: (win: Window): UploaderConfig => {
        return {
          authTokenHeader: 'Authorization',
          authToken: () => `Bearer ${win.localStorage.getItem(AUTH_TOKEN_KEY) || ''}`,
          method: 'POST',
          hasToastError: false,
        };
      },
      deps: [WINDOW],
    },
    {
      provide: FU_DIALOG_OK_CONFIG,
      useValue: {
        iconSize: '32',
        iconCloseName: 'close-square',
        iconCloseSize: '24',
      },
    },
    {
      provide: DICTIONARIES_API,
      useClass: environment.useMocks ? DictionariesMockApi : DictionariesApiService,
    },
    DictionariesStore,
    {
      provide: AUTH_API,
      useClass: environment.useMocks ? AuthApiMockService : AuthApiService,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideNgxMask(),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    importProvidersFrom(
      MatNativeDateModule,
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useClass: TranslateLoaderService,
          deps: [HttpClient],
        },
      }),
      NgxPermissionsModule.forRoot(),
    ),
  ],
};
